import React, { useCallback, useContext, useMemo } from "react";
import { HeaderA } from "components/headers";
import lang from "translations";
import { Path } from "paths";
import { VenueContext } from "contexts";
import { useApi, useMount, useFilter } from "hooks";
import { searchResourceType } from "apis";
import { ButtonLink, DataTable, Pill } from "components/commons";
import { PillType } from "enums";
import { prettifyVoucherStatus } from "services";
import { resourceType } from "mappers";
import { ModuleWrapper } from "components/fragments";

const ResourceTypeList = () => {
  const { venue } = useContext(VenueContext);
  const { venueId } = venue;

  const {
    request,
    loading = true,
    error,
    result = { data: [] },
    mappedData,
  } = useApi({
    api: searchResourceType,
    isArray: true,
    mapper: resourceType,
    returnMappedData: true,
  });

  const defaultFilter = {
    venueId,
    page: 1,
    pageSize: 20,
    searchKey: "",
  };
  const { modifyFilters, filterState } = useFilter(defaultFilter, defaultFilter);

  useMount(() => {
    request(filterState);
  });

  const fetchUpdateStore = useCallback(
    async (requestState) => {
      request(requestState);
    },
    [request]
  );

  const renderData = useMemo(() => {
    return mappedData.map(({ name, id, defaultCapacity, defaultCleanupDuration, shape, defaultReservationDuration, minimumSpentAmount, isActive }) => {
      return {
        name: <ButtonLink path={Path.RESOURCETYPE_ID(id)}>{name}</ButtonLink>,
        id: id,
        defaultCapacity: defaultCapacity ?? "-",
        defaultCleanupDuration: defaultCleanupDuration ?? "-",
        shape: shape ?? "-",
        defaultReservationDuration: defaultReservationDuration ?? "-",
        minimumSpentAmount: minimumSpentAmount ?? "-",
        isActive: (
          <Pill
            type={isActive ? PillType.Green : PillType.Opaque}
            size="text-xs"
          >
            {isActive ? prettifyVoucherStatus("ACTIVE") : prettifyVoucherStatus("INACTIVE")}
          </Pill>
        ),
      };
    });
  }, [mappedData]);

  return (
    <ModuleWrapper>
      <HeaderA
        title={lang.resourceTypes}
        description={lang.manageResourceTypes}
        button={{
          path: Path.RESOURCETYPE_CREATE,
          text: lang.createNewResourceType,
        }}
        className="mb-md"
      />
      <text>{result.metadata?.total} {lang.searchResults}</text>
      <DataTable
        page={filterState.page}
        pageSize={filterState.pageSize}
        onChangePage={modifyFilters}
        fetchList={fetchUpdateStore}
        total={result.metadata?.total}
        renderEmpty={{
          image: "https://pouch-inventory.s3.us-east-2.amazonaws.com/landing-image/no-resource-types.png",
          title: lang.createResourceTypeToManage,
        }}
        loading={loading}
        columns={[
          {
            key: "name",
            text: lang.name,
            custom: true,
          },
          {
            key: "defaultCapacity",
            text: lang.defaultCapacity,
          },
          {
            key: "defaultCleanupDuration",
            text: lang.defaultCleanupDuration,
          },
          {
            key: "shape",
            text: lang.shape,
          },
          {
            key: "defaultReservationDuration",
            text: lang.defaultReservationDuration,
          },
          {
            key: "minimumSpentAmount",
            text: lang.minimumSpentAmount,
          },
          {
            key: "isActive",
            text: lang.status,
          },
        ]}
        data={renderData}
        error={error}
      />
    </ModuleWrapper>
  );
};

export default ResourceTypeList;
